import type {
  FilterConditionDto,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdCondition
} from '@bindoc-projects/condition-domain-lib';

import type {
  FilterConditionPluginSpecification,
  FilterDetailedText,
} from '@bindoc-projects/filter-condition-domain-lib';
import {
  FilterConditionPluginImpl
} from '@bindoc-projects/filter-condition-domain-lib';


/**
 * The criteria of one group, as a reader wants them.
 *
 * These filters are switched on wholesale in practice, so a configured one
 * reports a run of criteria that all say the same thing — nine of them here,
 * which spelled out one per line is a wall of text that buries the year. A
 * group whose criteria ALL carry the same verdict is therefore written once
 * ("Alle Ausschlüsse erfüllt"), while a MIXED group stays item by item, because
 * there the individual criteria are exactly the information.
 *
 * Criteria that were never set (`null`) are omitted, as they always were: a
 * filter says nothing about what it does not constrain.
 */
function summariseCriteria(
  singular: string,
  plural: string,
  flags: ReadonlyArray<readonly [string, boolean | null]>,
): string[] {
  const set = flags.filter(
    (flag): flag is readonly [string, boolean] => flag[1] !== null,
  );
  if (set.length === 0) return [];

  const verdicts = new Set(set.map(([, value]) => value));
  if (set.length > 1 && verdicts.size === 1) {
    return [`Alle ${plural} ${set[0][1] ? 'erfüllt' : 'nicht erfüllt'}`];
  }

  return set.map(
    ([label, value]) =>
      `${singular} – ${label}: ${value ? 'Erfüllt' : 'Nicht erfüllt'}`,
  );
}

export const BD_AOP_CONDITION_ID = 'aop';
export const FilterConditionAopPluginType: FilterConditionPluginSpecification<
  FilterConditionAopPlugin,
  FilterConditionAopDto,
  BdAopCondition
> = {
  type: 'aop',
  category: 'case',
  displayName: { de: 'AOP Kriterien' },
  sortingIndex: 8,
  occurrence: 'multiple',
  fromObject(obj: FilterConditionAopDto): FilterConditionAopPlugin {
    const aopVersionYear = typeof obj.aopVersionYear === 'string'
      ? Number(obj.aopVersionYear.trim())
      : obj.aopVersionYear;
    return new FilterConditionAopPlugin(
        aopVersionYear,
        obj.abschnitt1 ?? null,
        obj.abschnitt2 ?? null,
        obj.abschnitt1Oder2 ?? null,
        obj.icdNichtAmbulant ?? null,
        obj.opsStationaer ?? null,
        obj.funktionseinschraenkungen ?? null,
        obj.pflegegrad ?? null,
        obj.beatmung ?? null,
        obj.altersgrenze ?? null,
        obj.beidseitig ?? null,
        obj.ophthalm ?? null,
        obj.aop ?? null,
      );
  },
  fromCondition(jsonObj: BdAopCondition): FilterConditionAopPlugin {
    const aopVersionYear = typeof jsonObj.aopVersionYear === 'string'
      ? Number((jsonObj.aopVersionYear as string).trim())
      : jsonObj.aopVersionYear;
    return new FilterConditionAopPlugin(
        aopVersionYear,
        jsonObj.abschnitt1 ?? null,
        jsonObj.abschnitt2 ?? null,
        jsonObj.abschnitt1Oder2 ?? null,
        jsonObj.icdNichtAmbulant ?? null,
        jsonObj.opsStationaer ?? null,
        jsonObj.funktionseinschraenkungen ?? null,
        jsonObj.pflegegrad ?? null,
        jsonObj.beatmung ?? null,
        jsonObj.altersgrenze ?? null,
        jsonObj.beidseitig ?? null,
        jsonObj.ophthalm ?? null,
        jsonObj.aop ?? null,
      );
  },
  newInstance(): FilterConditionAopPlugin {
    return new FilterConditionAopPlugin(
        FilterConditionAopPlugin.DEFAULT_AOP_VERSION_YEAR,
        null,
        null,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        true,
        null,
      );
  },
};

export class FilterConditionAopPlugin extends FilterConditionPluginImpl<
  FilterConditionAopPlugin,
  FilterConditionAopDto
> {
  public static DEFAULT_AOP_VERSION_YEAR = 2026;

  public static override Specification: FilterConditionPluginSpecification<
    FilterConditionAopPlugin,
    FilterConditionAopDto,
    BdAopCondition
  > = FilterConditionAopPluginType;

  constructor(
    private _aopVersionYear: number,
    private _abschnitt1: boolean | null = null,
    private _abschnitt2: boolean | null = null,
    private _abschnitt1Oder2: boolean | null = null,
    private _icdNichtAmbulant: boolean | null = null,
    private _opsStationaer: boolean | null = null,
    private _funktionseinschraenkungen: boolean | null = null,
    private _pflegegrad: boolean | null = null,
    private _beatmung: boolean | null = null,
    private _altersgrenze: boolean | null = null,
    private _beidseitig: boolean | null = null,
    private _ophthalm: boolean | null = null,
    private _aop: boolean | null = null,
  ) {
    super(FilterConditionAopPluginType.type);
  }

  public clone(): FilterConditionAopPlugin {
    return new FilterConditionAopPlugin(
      this._aopVersionYear,
      this._abschnitt1,
      this._abschnitt2,
      this._abschnitt1Oder2,
      this._icdNichtAmbulant,
      this._opsStationaer,
      this._funktionseinschraenkungen,
      this._pflegegrad,
      this._beatmung,
      this._altersgrenze,
      this._beidseitig,
      this._ophthalm,
      this._aop,
    );
  }

  public override equals(toCompare: FilterConditionAopPlugin): boolean {
    return (
      super.equals(toCompare) &&
      this._aopVersionYear === toCompare._aopVersionYear &&
      this._abschnitt1 === toCompare.abschnitt1 &&
      this._abschnitt2 === toCompare.abschnitt2 &&
      this._abschnitt1Oder2 === toCompare.abschnitt1Oder2 &&
      this._icdNichtAmbulant === toCompare.icdNichtAmbulant &&
      this._opsStationaer === toCompare.opsStationaer &&
      this._funktionseinschraenkungen === toCompare.funktionseinschraenkungen &&
      this._pflegegrad === toCompare.pflegegrad &&
      this._beatmung === toCompare.beatmung &&
      this._altersgrenze === toCompare.altersgrenze &&
      this._beidseitig === toCompare.beidseitig &&
      this._ophthalm === toCompare.ophthalm &&
      this._aop === toCompare.aop
    );
  }

  override valid(): boolean {
    return !!this._aopVersionYear;
  }

  override validationToText(): string {
    return '';
  }

  public toCondition(): BdAopCondition {
    return new BdAopCondition(
        this._aopVersionYear,
        this._abschnitt1,
        this._abschnitt2,
        this._abschnitt1Oder2,
        this._icdNichtAmbulant,
        this._opsStationaer,
        this._funktionseinschraenkungen,
        this._pflegegrad,
        this._beatmung,
        this._altersgrenze,
        this._beidseitig,
        this._ophthalm,
        this._aop,
      );
  }

  toJSON(): FilterConditionAopDto | null {
    return {
      type: this.type,
      aopVersionYear: this._aopVersionYear,
      abschnitt1: this._abschnitt1,
      abschnitt2: this._abschnitt2,
      abschnitt1Oder2: this._abschnitt1Oder2,
      icdNichtAmbulant: this._icdNichtAmbulant,
      opsStationaer: this._opsStationaer,
      funktionseinschraenkungen: this._funktionseinschraenkungen,
      pflegegrad: this._pflegegrad,
      beatmung: this._beatmung,
      altersgrenze: this._altersgrenze,
      beidseitig: this._beidseitig,
      ophthalm: this._ophthalm,
      aop: this._aop,
    };
  }

  toText(): string | null {
    return 'AOP Kriterien';
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;

    const items: string[] = [`AOP Jahr: ${this._aopVersionYear}`];

    const includes: Array<[string, boolean | null]> = [
      ['AOP-Katalog I oder II', this._abschnitt1Oder2],
    ];
    const excludes: Array<[string, boolean | null]> = [
      ['ICD (stationär)', this._icdNichtAmbulant],
      ['OPS (stationär)', this._opsStationaer],
      ['Funktionseinschränkungen (Barthel-Index)', this._funktionseinschraenkungen],
      ['Pflegegrad', this._pflegegrad],
      ['Beatmung', this._beatmung],
      ['Altersgrenze (> 1 Jahr)', this._altersgrenze],
      ['Beidseitige Eingriffe', this._beidseitig],
      ['Ophthalmologische Begleiterkrankungen', this._ophthalm],
    ];

    items.push(...summariseCriteria('Einschluss', 'Einschlüsse', includes));
    items.push(...summariseCriteria('Ausschluss', 'Ausschlüsse', excludes));

    return { title, items };
  }

  toValue(): FilterConditionAopPlugin {
    return this;
  }

  get abschnitt1(): boolean | null {
    return this._abschnitt1;
  }

  set abschnitt1(value: boolean | null) {
    this._abschnitt1 = value;
  }

  get abschnitt2(): boolean | null {
    return this._abschnitt2;
  }

  set abschnitt2(value: boolean | null) {
    this._abschnitt2 = value;
  }

  get icdNichtAmbulant(): boolean | null {
    return this._icdNichtAmbulant;
  }

  set icdNichtAmbulant(value: boolean | null) {
    this._icdNichtAmbulant = value;
  }

  get opsStationaer(): boolean | null {
    return this._opsStationaer;
  }

  set opsStationaer(value: boolean | null) {
    this._opsStationaer = value;
  }

  get funktionseinschraenkungen(): boolean | null {
    return this._funktionseinschraenkungen;
  }

  set funktionseinschraenkungen(value: boolean | null) {
    this._funktionseinschraenkungen = value;
  }

  get pflegegrad(): boolean | null {
    return this._pflegegrad;
  }

  set pflegegrad(value: boolean | null) {
    this._pflegegrad = value;
  }

  get beatmung(): boolean | null {
    return this._beatmung;
  }

  set beatmung(value: boolean | null) {
    this._beatmung = value;
  }

  get altersgrenze(): boolean | null {
    return this._altersgrenze;
  }

  set altersgrenze(value: boolean | null) {
    this._altersgrenze = value;
  }

  get beidseitig(): boolean | null {
    return this._beidseitig;
  }

  set beidseitig(value: boolean | null) {
    this._beidseitig = value;
  }

  get ophthalm(): boolean | null {
    return this._ophthalm;
  }

  set ophthalm(value: boolean | null) {
    this._ophthalm = value;
  }

  get aopVersionYear(): number {
    return this._aopVersionYear;
  }

  set aopVersionYear(value: number) {
    this._aopVersionYear = value;
  }

  get abschnitt1Oder2(): boolean | null {
    return this._abschnitt1Oder2;
  }

  set abschnitt1Oder2(value: boolean | null) {
    this._abschnitt1Oder2 = value;
  }

  get aop(): boolean | null {
    return this._aop;
  }

  set aop(value: boolean | null) {
    this._aop = value;
  }
}

export class BdAopCondition extends BdCondition {
  public aopVersionYear: number;

  constructor(
    aopVersionYear: string | number,
    public abschnitt1: boolean | null,
    public abschnitt2: boolean | null,
    public abschnitt1Oder2: boolean | null,
    public icdNichtAmbulant: boolean | null,
    public opsStationaer: boolean | null,
    public funktionseinschraenkungen: boolean | null,
    public pflegegrad: boolean | null,
    public beatmung: boolean | null,
    public altersgrenze: boolean | null,
    public beidseitig: boolean | null,
    public ophthalm: boolean | null,
    public aop: boolean | null,
  ) {
    super(BD_AOP_CONDITION_ID);
    this.aopVersionYear = typeof aopVersionYear === 'string'
      ? Number(aopVersionYear.trim())
      : aopVersionYear;
  }

  toJSON(): FilterConditionDto {
    return {
      [this.id]: {
        aopVersionYear: this.aopVersionYear,
        abschnitt1: this.abschnitt1,
        abschnitt2: this.abschnitt2,
        abschnitt1Oder2: this.abschnitt1Oder2,
        icdNichtAmbulant: this.icdNichtAmbulant,
        opsStationaer: this.opsStationaer,
        funktionseinschraenkungen: this.funktionseinschraenkungen,
        pflegegrad: this.pflegegrad,
        beatmung: this.beatmung,
        altersgrenze: this.altersgrenze,
        beidseitig: this.beidseitig,
        ophthalm: this.ophthalm,
        aop: this.aop,
      },
    };
  }
}

interface FilterConditionAopDto {
  type: string;
  aopVersionYear: string | number;
  abschnitt1: boolean;
  abschnitt2: boolean;
  abschnitt1Oder2: boolean;
  icdNichtAmbulant: boolean;
  opsStationaer: boolean;
  funktionseinschraenkungen: boolean;
  pflegegrad: boolean;
  beatmung: boolean;
  altersgrenze: boolean;
  beidseitig: boolean;
  ophthalm: boolean;
  aop: boolean;
}
